import * as React from 'react';
import { useQuery, useMutation, MutationResult } from '@apollo/client';

import * as GetForm from 'core/gql/queries/show-form';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as DuplicateReg2Form from 'core/gql/mutations/duplicate-reg-2-form';
import * as GqlForms from 'core/gql/queries/get-all-forms-reg';
import { NgbIntegration } from '../hooks/useApiGetNgbIntegrations';
import { DuplicateReg2FormModalInputs } from './DuplicateReg2FormModal';
import { parse } from 'path';

interface DuplicateOptionsState {
  name: string;
  disabled: boolean;
  isPartial: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children?: DuplicateOptionsState[];
}

const options = {
  registration_options: [
    'participant_groups',
    'registration_fees',
    'eligibility_rules',
    'capacity_limit_and_waitlist',
  ],
  form_builder: ['questions_and_logic', 'digital_agreements'],
  checkout_settings: ['installment_plans', 'discounts'],
  addons: [],
  confirmation_message: [],
};

export const useDuplicateRegForm = ({
  orgId,
}): [Function, MutationResult<any>] => {
  const [duplicateRegForm, requestState] = useMutation(
    DuplicateReg2Form.MUTATION,
    {
      refetchQueries: [
        { query: GqlForms.QUERY, variables: { organizationId: orgId } },
      ],
    }
  );

  const duplicateForm = async (variables) =>
    await duplicateRegForm({
      variables: { input: variables },
    });

  return [duplicateForm, requestState];
};

export const useFormHasRegistrationFees = ({ formId, orgId }) => {
  const { loading, data } = useQuery(GetForm.QUERY, {
    variables: { formId: +formId, organizationId: +orgId },
    skip: !formId,
  });

  const hasRegistrationFees = React.useMemo(() => {
    if (loading && !data) {
      return null;
    } else if (!loading && data) {
      return data.showRegistrationForm.participant_groups?.some(
        (p) => p.participant_fee > 0
      );
    }
  }, [data, loading]);

  return hasRegistrationFees;
};

export const useFormHasAddOnFees = ({ formId }) => {
  const { loading, data } = useQuery(RegistrationFormAdjustments.QUERY, {
    variables: { formId: +formId },
    skip: !formId,
  });

  const hasAddOnFees = React.useMemo(() => {
    if (loading && !data) {
      return null;
    } else if (!loading && data) {
      return data.registrationFormAdjustments.some(
        (f) => f.formAdjustmentDefinition.type !== 'discount'
      );
    }
  }, [data, loading]);

  return hasAddOnFees;
};

export const useDuplicateOptions = ({
  form,
  hasAddOnFees,
  hasRegistrationFees,
}) => {
  const { getValues, setValue } = form;

  const values = getValues();

  const uncheckSection = (parentFieldName) => {
    setValue(parentFieldName, false);
    options[parentFieldName]?.forEach((child) => {
      setValue(child, false);
    });
  };

  React.useEffect(() => {
    if (
      hasAddOnFees &&
      hasRegistrationFees &&
      values.registration_fees === false &&
      values.questions_and_logic === false
    ) {
      uncheckSection('checkout_settings');
    }
    if (
      !hasAddOnFees &&
      hasRegistrationFees &&
      values.registration_fees === false
    ) {
      uncheckSection('checkout_settings');
    }
    if (
      hasAddOnFees &&
      !hasRegistrationFees &&
      values.questions_and_logic === false
    ) {
      uncheckSection('checkout_settings');
    }
  }, [values.registration_fees, values.questions_and_logic]);

  React.useEffect(() => {
    if (values.participant_groups === false) {
      uncheckSection('registration_options');
    }
  }, [values.participant_groups]);

  const duplicateOptions = React.useMemo(() => {
    const isDisabled = (name) => {
      switch (name) {
        case 'registration_fees':
          return values.participant_groups === false;
        case 'eligibility_rules':
          return values.participant_groups === false;
        case 'capacity_limit_and_waitlist':
          return values.participant_groups === false;
        case 'checkout_settings':
          if (hasAddOnFees && hasRegistrationFees) {
            return (
              values.registration_fees === false &&
              values.questions_and_logic === false
            );
          } else if (!hasAddOnFees && hasRegistrationFees) {
            return values.registration_fees === false;
          } else if (hasAddOnFees && !hasRegistrationFees) {
            return values.questions_and_logic === false;
          }
          break;
        case 'installment_plans':
          if (hasAddOnFees && hasRegistrationFees) {
            return (
              values.registration_fees === false &&
              values.questions_and_logic === false
            );
          } else if (!hasAddOnFees && hasRegistrationFees) {
            return values.registration_fees === false;
          } else if (hasAddOnFees && !hasRegistrationFees) {
            return values.questions_and_logic === false;
          }
          break;
        case 'discounts':
          if (hasAddOnFees && hasRegistrationFees) {
            return (
              values.registration_fees === false &&
              values.questions_and_logic === false
            );
          } else if (!hasAddOnFees && hasRegistrationFees) {
            return values.registration_fees === false;
          } else if (hasAddOnFees && !hasRegistrationFees) {
            return values.questions_and_logic === false;
          }
          break;
        default:
          return false;
      }
    };

    const atLeastOneChecked = (fieldNames: string[]): boolean => {
      const fields = fieldNames?.map((name) => ({
        [name]: values[name],
      }));

      const allChecked = fields?.every(
        (value) => Object.values(value)[0] === true
      );
      return allChecked ? false : fieldNames?.some((name) => values[name]);
    };

    // Used to check if parent should be unchecked, since all children are unchecked
    const noneChecked = (
      fieldNames: string[],
      latestUpdatedValue: { [key: string]: boolean }
    ): boolean => {
      const fields = fieldNames?.map((name) => {
        if (name === Object.keys(latestUpdatedValue)[0]) {
          return latestUpdatedValue;
        } else {
          return {
            [name]: values[name],
          };
        }
      });

      return fields?.every((value) => Object.values(value)[0] === false);
    };

    const isPartial = (fieldName) => atLeastOneChecked(options[fieldName]);

    const updateOtherCheckboxes = (fieldName, isFieldChecked) => {
      // If parent bulk select/unselect of children
      const parents = Object.keys(options);
      if (parents.includes(fieldName)) {
        options[fieldName]?.forEach((child) => {
          setValue(child, isFieldChecked);
        });
      }

      // If all children are unchecked, uncheck parent
      const children = Object.values(options).flat();
      if (children.includes(fieldName)) {
        const parent = Object.keys(options).find((key) =>
          options[key].includes(fieldName)
        );
        if (noneChecked(options[parent], { [fieldName]: isFieldChecked })) {
          setValue(parent, false);
        }
      }
    };

    const createDuplicateOptions = (
      options: Record<string, string[]>
    ): DuplicateOptionsState[] => {
      const duplicateOptions: DuplicateOptionsState[] = [];

      Object.entries(options).forEach(([name, children]) => {
        const option: DuplicateOptionsState = {
          name,
          disabled: isDisabled(name),
          isPartial: isPartial(name),
          children: [],
          onChange: (e) => {
            updateOtherCheckboxes(e.target.name, e.target.checked);
          },
        };

        children.forEach((child) => {
          const childOption: DuplicateOptionsState = {
            name: child,
            disabled: isDisabled(child),
            isPartial: false,
            onChange: (e) => {
              // If child is checked, check parent
              const parent = Object.keys(options).find((key) =>
                options[key].includes(e.target.name)
              );
              setValue(parent, true);
              updateOtherCheckboxes(e.target.name, e.target.checked);
            },
          };

          option.children.push(childOption);
        });

        duplicateOptions.push(option);
      });

      return duplicateOptions;
    };

    let duplicateOptions = createDuplicateOptions(options);

    if (!hasRegistrationFees && !hasAddOnFees) {
      duplicateOptions = duplicateOptions.filter(
        (f) => f.name !== 'checkout_settings'
      );
    }

    return duplicateOptions;
  }, [hasRegistrationFees, hasAddOnFees, values]);

  return duplicateOptions;
};

export const buildSettingsDefaultFormOptions = () => {
  const optionFormDefaults = {};
  Object.keys(options).forEach((key) => {
    optionFormDefaults[key] = true;
    if (Array.isArray(options[key])) {
      options[key].forEach((item) => {
        optionFormDefaults[item] = true;
      });
    }
  });
  return optionFormDefaults;
};

export const buildSettingsToDuplicate = (values: {
  [key: string]: boolean;
}) => {
  const settings = [];
  Object.keys(values).forEach((key) => {
    if (values[key] === true) {
      settings.push(key);
    }
  });

  return settings.filter((setting) =>
    [
      'participant_groups',
      'registration_fees',
      'eligibility_rules',
      'capacity_limit_and_waitlist',
      'questions_and_logic',
      'digital_agreements',
      'discounts',
      'confirmation_message',
      'installment_plans',
      'addons'
    ].includes(setting)
  );
};

export function buildNgbIntegrationData(
  code: NgbIntegration['code'],
  formData: DuplicateReg2FormModalInputs
) {
  switch (code) {
    case 'usa_lacrosse':
      return {
        ngb_integrations: [
          {
            ngb_integration_id: Number(formData.ngbIntegrationId),
            enabled: formData.enabled,
            seasonEndDate:
              formData.enabled && formData.season_dates
                ? new Date(formData.season_dates).toISOString()
                : '',
          },
        ],
      };
    case 'usa_hockey': {
      return parseHockeySeasonDates(formData);
    }
    case 'hockey_canada': {
      return parseHockeySeasonDates(formData);
    }
    default:
      return null;
  }
}

const parseHockeySeasonDates = (formData) => {
  const [seasonStartDate, seasonEndDate] = formData.season_dates
  ? formData.season_dates.split('|')
  : [];
  return {
    ngb_integrations: [
      {
        ngb_integration_id: Number(formData.ngbIntegrationId),
        enabled: formData.enabled,
        seasonStartDate:
          formData.enabled && seasonStartDate
            ? new Date(seasonStartDate).toISOString()
            : '',
        seasonEndDate:
          formData.enabled && seasonEndDate
            ? new Date(seasonEndDate).toISOString()
            : '',
      },
    ],
  };
}
